import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/services/api';

export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  organization?: {
    id: string;
    name: string;
    code: string;
    logo?: string;
  };
  branch?: {
    id: string;
    name: string;
  };
  department?: {
    id: string;
    name: string;
  };
  is2FAEnabled: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  requires2FA: boolean;
  pendingUserId: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (userId: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setPermissions: (permissions: string[]) => void;
  checkAuth: () => Promise<void>;
  
  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: true,
      requires2FA: false,
      pendingUserId: null,

      login: async (email: string, password: string) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const data = response.data;

          if (data.requires2FA) {
            set({
              requires2FA: true,
              pendingUserId: data.userId,
            });
            return;
          }

          set({
            user: data.user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            permissions: data.permissions || [],
            isAuthenticated: true,
            requires2FA: false,
            pendingUserId: null,
          });
        } catch (error) {
          throw error;
        }
      },

      verify2FA: async (userId: string, token: string) => {
        try {
          const response = await api.post('/auth/2fa/verify', { userId, token });
          const data = response.data;

          set({
            user: data.user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            permissions: data.permissions || [],
            isAuthenticated: true,
            requires2FA: false,
            pendingUserId: null,
          });
        } catch (error) {
          throw error;
        }
      },

      logout: async () => {
        try {
          const refreshToken = get().refreshToken;
          if (refreshToken) {
            await api.post('/auth/logout', { refreshToken });
          }
        } catch (error) {
          // Ignore logout errors
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            permissions: [],
            isAuthenticated: false,
            requires2FA: false,
            pendingUserId: null,
          });
        }
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      setUser: (user: User) => {
        set({ user });
      },

      setPermissions: (permissions: string[]) => {
        set({ permissions });
      },

      checkAuth: async () => {
        const accessToken = get().accessToken;
        
        if (!accessToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get('/auth/me', { _skipErrorToast: true } as never);
          set({
            user: response.data.user,
            permissions: response.data.permissions || [],
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Stale or invalid tokens — clear everything silently
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            permissions: [],
            isAuthenticated: false,
            isLoading: false,
            requires2FA: false,
            pendingUserId: null,
          });
        }
      },

      // Permission helper methods
      hasPermission: (permission: string) => {
        const { permissions, user } = get();
        // Super admin has all permissions
        if (user?.role === 'SUPER_ADMIN') return true;
        return permissions.includes(permission);
      },

      hasAnyPermission: (...requiredPermissions: string[]) => {
        const { permissions, user } = get();
        if (user?.role === 'SUPER_ADMIN') return true;
        return requiredPermissions.some((p) => permissions.includes(p));
      },

      hasAllPermissions: (...requiredPermissions: string[]) => {
        const { permissions, user } = get();
        if (user?.role === 'SUPER_ADMIN') return true;
        return requiredPermissions.every((p) => permissions.includes(p));
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.checkAuth();
        }
      },
    }
  )
);
